import React, { useEffect, useRef, useState } from "react";
import { Button, Tooltip, message } from "antd";
import { AudioOutlined, StopOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons";

// Records a short voice note via the browser's MediaRecorder API, lets the
// user listen to it before committing, and hands the parent a plain `File`
// on confirm -- the parent then treats it exactly like any other attachment
// (e.g. pushing it into the same antd Upload fileList already used for
// images/PDFs), so none of the existing attach/remove/5-file-cap/FormData
// plumbing needs to know this came from a microphone instead of a file
// picker. Nothing like this existed in the codebase before.
const pickMimeType = () => {
  if (typeof window === "undefined" || !window.MediaRecorder) return "";
  // Order matters: webm is what Chrome/Firefox/Edge actually record in;
  // mp4 is Safari's. ogg is a fallback some browsers also support.
  const candidates = ["audio/webm", "audio/mp4", "audio/ogg"];
  return candidates.find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
};

const extensionFor = (mimeType) => {
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mp4")) return "m4a";
  return "webm";
};

const formatElapsed = (seconds) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

const VoiceRecorder = ({ onRecorded, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const previewBlobRef = useRef(null);
  const timerRef = useRef(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  // Cleanup-on-unmount only, intentionally not re-running when previewUrl
  // changes (previewUrl is read via a ref-captured closure at unmount time
  // either way, since this is the unmount cleanup, not a per-render effect).
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      stopStream();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      message.error("Voice recording isn't supported in this browser");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
        previewBlobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
        stopStream();
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      message.error("Microphone access was denied or unavailable");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewBlobRef.current = null;
    setPreviewUrl(null);
    setElapsed(0);
  };

  const confirm = () => {
    const blob = previewBlobRef.current;
    if (!blob) return;

    const file = new File(
      [blob],
      `voice-note-${Date.now()}.${extensionFor(blob.type)}`,
      { type: blob.type || "audio/webm" }
    );
    onRecorded(file);
    discard();
  };

  if (previewUrl) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <audio controls src={previewUrl} style={{ height: 32, width: 300 }} />
        <Tooltip title="Attach this recording">
          <Button size="small" icon={<CheckOutlined />} onClick={confirm} />
        </Tooltip>
        <Tooltip title="Discard and re-record">
          <Button size="small" danger icon={<DeleteOutlined />} onClick={discard} />
        </Tooltip>
      </div>
    );
  }

  if (recording) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#ff4d4f", fontSize: 12 }}>
          <span
            style={{
              width: 8, height: 8, borderRadius: "50%", background: "#ff4d4f",
              animation: "voice-recorder-pulse 1s ease-in-out infinite",
            }}
          />
          Recording {formatElapsed(elapsed)}
        </span>
        <style>{`@keyframes voice-recorder-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }`}</style>
        <Button size="small" danger icon={<StopOutlined />} onClick={stopRecording}>
          Stop
        </Button>
      </div>
    );
  }

  return (
    <Button
      icon={<AudioOutlined />}
      size="small"
      onClick={startRecording}
      disabled={disabled}
      style={{ marginTop: 10, marginLeft: 8 }}
    >
      Record voice note
    </Button>
  );
};

export default VoiceRecorder;
